import { createContext } from 'preact';
import { useContext, useState, useCallback } from 'preact/hooks';

interface NavigationState {
    isPending: boolean;
    pendingPath: string | null;
    startNavigation: (path: string) => void;
    completeNavigation: () => void;
    cancelNavigation: () => void;
}

const NavigationStateContext = createContext<NavigationState>({
    isPending: false,
    pendingPath: null,
    startNavigation: () => {},
    completeNavigation: () => {},
    cancelNavigation: () => {},
});

export function NavigationStateProvider({ children }: { children: preact.ComponentChildren }) {
    const [isPending, setIsPending] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);

    const startNavigation = useCallback((path: string) => {
        setIsPending(true);
        setPendingPath(path);
    }, []);

    const completeNavigation = useCallback(() => {
        setIsPending(false);
        setPendingPath(null);
    }, []);

    const cancelNavigation = useCallback(() => {
        setIsPending(false);
        setPendingPath(null);
    }, []);

    const value: NavigationState = {
        isPending,
        pendingPath,
        startNavigation,
        completeNavigation,
        cancelNavigation,
    };

    return (
        <NavigationStateContext.Provider value={value}>
            {children}
        </NavigationStateContext.Provider>
    );
}

export function useNavigationState(): NavigationState {
    return useContext(NavigationStateContext);
}
